import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { useMask } from '@atlas/react-forms';
import { FieldHint, Input } from '@atlas/ui';

import Field from 'common/Form/Field';
import { getIsFieldRequired } from 'common/utils/utils';

const TextField = ({ className, field, onBlur = () => {}, onChange, onFocus = () => {} }) => {
  const {
    active,
    autofocus,
    checkOnChanges,
    disabled,
    errors,
    hint,
    isModifiable = true,
    label,
    maskOption,
    name,
    placeholder,
    rules,
    touched,
    trim,
    value,
    warnings,
  } = field;

  const hasMask = Boolean(maskOption);

  const {
    handleBeforeInput,
    handleChange: handleMaskChange,
    handleKeyDown,
    maskedValue,
    value: cleanValue,
  } = useMask({
    mask: maskOption,
  });

  const [activeField, setActiveField] = useState(active);
  const error = (touched || (checkOnChanges && active)) && errors[0];
  const success = touched && !error;
  const warning = touched ? warnings?.[0]?.message : null;

  const isRequiredSymbol = useMemo(() => getIsFieldRequired(rules), [rules]);

  const handleDirectChange = useCallback(
    e => {
      const newValue = trim ? e.target.value.trim() : e.target.value;
      onChange(newValue);
    },
    [onChange, trim],
  );

  const handleFocus = useCallback(() => {
    setActiveField(true);
    onFocus();
  }, [onFocus]);

  const handleBlur = useCallback(() => {
    setActiveField(false);
    onBlur();
  }, [onBlur]);

  const inputMode = useMemo(
    () => (field.name.toLowerCase() === 'email' ? 'email' : 'text'),
    [field.name],
  );
  const type = useMemo(() => (field.name === 'email' ? 'email' : 'text'), [field.name]);

  useEffect(() => {
    if (!hasMask) return;
    const effectiveCleanValue = trim ? cleanValue.trim() : cleanValue;
    if (value && value !== effectiveCleanValue) {
      handleMaskChange({
        target: {
          selectionStart: String(value).length,
          value: String(value),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleMaskChange, hasMask, value, trim]);

  // Notify parent of cleanValue changes when mask is active
  useEffect(() => {
    if (!hasMask) return;
    onChange(trim ? cleanValue.trim() : cleanValue);
  }, [cleanValue, hasMask, onChange, trim]);

  return (
    <Field isSignupForm options={field}>
      <Input
        active={activeField}
        autoFocus={autofocus}
        className={className}
        disabled={disabled || !isModifiable}
        error={error}
        inputMode={inputMode}
        isRequiredSymbol={isRequiredSymbol}
        label={label}
        name={name}
        onBeforeInput={hasMask ? handleBeforeInput : undefined}
        onBlur={handleBlur}
        onChange={hasMask ? handleMaskChange : handleDirectChange}
        onFocus={handleFocus}
        onKeyDown={hasMask ? handleKeyDown : undefined}
        placeholder={maskOption || placeholder}
        success={success}
        touched={touched}
        type={type}
        value={hasMask ? maskedValue : value || ''}
        warning={warning}
      />
      {!disabled && <FieldHint hint={hint} showOnClick />}
    </Field>
  );
};

TextField.propTypes = {
  className: PropTypes.string,
  field: PropTypes.object.isRequired,
  onBlur: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
};

export default TextField;
